import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { Custom } from "@/types/api/api-types";
import { CotationUpdateBoFormData } from "@/types/forms/bo/cotationUpdateBoForm.form";
import StyledInput from "@/components/styled/StyledInput";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { AutocompleteSearch } from "../autocompleteSearch";
import ExpressIcon from "@/assets/icons/express.svg?react";
import { CountryCode } from "libphonenumber-js";
import { StyledSelectSingleValue } from "@/components/styled/StyledSelectSingleValue";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { useTranslation } from "react-i18next";

interface InfoClientsProps {
  customs: Custom[] | undefined;
  selectedCustomId: string;
  destinationCountry: string;
  hasCompanyName: boolean;
  isBackOffice?: boolean;
  forceRenrenderAutocomplete?: number;
}

export const InfoClients = ({
  customs,
  selectedCustomId,
  destinationCountry,
  hasCompanyName,
  isBackOffice = false,
  forceRenrenderAutocomplete = 0,
}: InfoClientsProps) => {
  const { t } = useTranslation("boExpeditions");
  const methods = useFormContext<CotationUpdateBoFormData>();
  const countryCode = customs?.find((custom) => custom.id === selectedCustomId)?.flagCode || "";
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handlePlaceClicked = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      methods.setValue("street", parsed.street);
      methods.setValue("postalCode", parsed.postalCode);
      methods.setValue("city", parsed.city);

      if (showStateOrProvince) {
        methods.setValue("stateOrProvinceCode", parsed.stateOrProvinceCode);
      }

      methods.trigger([
        "street",
        "postalCode",
        "city",
        ...(showStateOrProvince ? (["stateOrProvinceCode"] as const) : []),
      ]);
    },
    [methods, showStateOrProvince, countryCode],
  );

  const handleStreetInput = (e: string) => {
    methods.setValue("street", e);
    methods.trigger(["street"]);
  };

  return (
    <div className="shadow-basic flex w-full flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6 md:max-w-[1000px]">
      <h2 className="text-brand700 text-xl font-semibold">
        {t("pendingShipmentsClientInfo.title")}
      </h2>
      <div className="flex w-full flex-col gap-2">
        {hasCompanyName && (
          <StyledInput
            control={methods.control}
            name="company"
            title={t("pendingShipmentsClientInfo.companyLabel")}
            className="w-full"
            isReadOnly={true}
          />
        )}
        <div className="flex flex-col gap-2 md:flex-row">
          <StyledInput
            control={methods.control}
            name="lastName"
            title={t("pendingShipmentsClientInfo.lastNameLabel")}
            className="w-full"
          />
          <StyledInput
            control={methods.control}
            name="firstName"
            title={t("pendingShipmentsClientInfo.firstNameLabel")}
            className="w-full"
          />
        </div>
        <div className="md:grid-cols-rows2 grid gap-2 md:grid-cols-2 md:grid-rows-1 md:items-start">
          <StyledPhoneInput
            control={methods.control}
            name="phone"
            title={t("pendingShipmentsClientInfo.phoneLabel")}
            defaultCountry={(destinationCountry as CountryCode) || "FR"}
          />
          <StyledInput
            control={methods.control}
            name="email"
            title={t("pendingShipmentsClientInfo.emailLabel")}
          />
        </div>
        <AutocompleteSearch
          key={forceRenrenderAutocomplete}
          placeholder={t("pendingShipmentsClientInfo.streetPlaceholder")}
          countryCode={customs?.find((custom) => custom.id === selectedCustomId)?.countryCode}
          onPlaceClicked={handlePlaceClicked}
          control={methods.control}
          handleStreetInput={handleStreetInput}
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <StyledInput
            className="flex-1"
            control={methods.control}
            name="city"
            title={t("pendingShipmentsClientInfo.cityLabel")}
          />
          {showPostalCode && (
            <StyledInput
              className="flex-1"
              control={methods.control}
              name="postalCode"
              title={t("pendingShipmentsClientInfo.postalCodeLabel")}
            />
          )}
          {showStateOrProvince && (
            <StyledInput
              className="flex-1"
              control={methods.control}
              name="stateOrProvinceCode"
              title={t("pendingShipmentsClientInfo.stateOrProvinceLabel")}
            />
          )}
        </div>
        <div className="flex flex-col justify-between gap-4 md:flex-row">
          <StyledSelectSingleValue
            className="w-full"
            isDisabled={true}
            control={methods.control}
            name="customId"
            title={t("pendingShipmentsClientInfo.customLabel")}
            setValue={(e: any) => methods.setValue("customId", e)}
            disabled
            options={
              customs?.map((custom) => ({
                value: custom.id,
                label: custom.label,
              })) || []
            }
          />
          {isBackOffice && (
            <div className="flex flex-col-reverse items-center justify-center gap-2">
              <StyledSwitch name="dapOption" control={methods.control} />
              <div className="rounded-xl bg-purple-600 px-3 py-1 text-white">DAP</div>
            </div>
          )}
          {isBackOffice && (
            <div className="flex flex-col-reverse items-center justify-center gap-2">
              <StyledSwitch name="express" control={methods.control} />
              <div className="bg-brand600 flex items-center gap-2 rounded-xl px-3 py-1">
                <ExpressIcon />
                <p className="text-white">{t("pendingShipmentsClientInfo.expressLabel")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
